// Fee & Obligation PR-2 — guided obligation waive. Explains the consequence
// before the click (remaining balance written off, recovered money stands,
// the linked fee row resolves alongside — the both-representations contract),
// requires a reason, and confirms what actually happened from the server's
// result (fees resolved, statements regenerated). Refusals (collected fee,
// posted statement, paid-in-full obligation) come back as a 400 whose message
// IS the explanation — rendered verbatim.

import { useState } from "react";
import { Button, Textarea } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { formatCurrency } from "@/design-system/lib/format";
import type {
  ObligationLedgerRowDto,
  ObligationWaiveResultDto,
} from "@/api/portfolioAdmin/obligationLedgerApi";
import { useWaiveObligation } from "@/hooks/portfolioAdmin/useObligationLedger";

export function WaiveObligationModal({
  loanId,
  row,
  onClose,
}: {
  loanId: string;
  row: ObligationLedgerRowDto;
  onClose: () => void;
}) {
  const waive = useWaiveObligation(loanId);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ObligationWaiveResultDto | null>(null);

  const trimmed = reason.trim();

  const submit = async () => {
    setError(null);
    try {
      const res = await waive.mutateAsync({
        obligationId: row.obligationId,
        body: { reason: trimmed },
      });
      setResult(res);
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : "The obligation could not be waived.",
      );
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Waive obligation"
      description="Writes off what's still owed. Money already recovered stands."
      footer={
        result ? (
          <Button onClick={onClose} data-testid="waive-done">
            Done
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={waive.isPending}
              data-testid="waive-cancel"
            >
              Cancel
            </Button>
            <Button
              loading={waive.isPending}
              disabled={trimmed.length === 0 || waive.isPending}
              onClick={() => void submit()}
              data-testid="waive-submit"
            >
              Waive {formatCurrency(row.remaining)}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className="flex flex-col gap-space-2" data-testid="waive-success">
          <p className="text-small text-onyx m-0">
            {formatCurrency(result.waivedAmount)} waived
            {result.recoveredKept > 0
              ? ` — ${formatCurrency(result.recoveredKept)} already recovered stands.`
              : "."}
          </p>
          {result.resolvedFeeIds.length > 0 && (
            <p className="text-small text-onyx m-0" data-testid="waive-fees-resolved">
              {result.resolvedFeeIds.length === 1
                ? "The linked fee entry was waived alongside it."
                : `${result.resolvedFeeIds.length} linked fee entries were waived alongside it.`}
            </p>
          )}
          {result.regeneratedStatementIds.length > 0 && (
            <p
              className="text-small text-status-warning m-0"
              role="status"
              data-testid="waive-statements-regenerated"
            >
              {result.regeneratedStatementIds.length === 1
                ? "A statement that billed the fee was regenerated — it re-enters TPS review without the charge."
                : `${result.regeneratedStatementIds.length} statements that billed the fee were regenerated — they re-enter TPS review without the charge.`}
            </p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-space-3" data-testid="waive-form">
          <dl className="grid grid-cols-[10rem_1fr] gap-y-space-2 text-small text-onyx m-0 p-space-3 border border-border-light rounded-md bg-white-warm">
            <dt className="text-gray-mid">Obligation</dt>
            <dd className="m-0 font-semibold">{row.typeDisplay || row.typeCode}</dd>
            <dt className="text-gray-mid">Remaining (waived)</dt>
            <dd className="m-0 tabular-nums">{formatCurrency(row.remaining)}</dd>
            <dt className="text-gray-mid">Recovered (stands)</dt>
            <dd className="m-0 tabular-nums">{formatCurrency(row.recoveredToDate)}</dd>
          </dl>

          <p className="text-small text-onyx m-0" data-testid="waive-consequence">
            Waiving stops all future collection of this obligation and resolves
            the linked fee entry with it, so the fee ledger and the waterfall
            agree. If the fee already bills on a statement still in review, that
            statement is regenerated without the charge; a posted or paid
            statement blocks the waive instead.
          </p>

          <div>
            <label
              htmlFor="waive-reason"
              className="block text-caption uppercase tracking-label text-gray-mid mb-space-1"
            >
              Reason (required)
            </label>
            <Textarea
              id="waive-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this being waived?"
              data-testid="waive-reason"
            />
          </div>

          {error && (
            <p
              className="text-small text-status-error m-0"
              role="alert"
              data-testid="waive-error"
            >
              {error}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
